import { Col, DatePicker, Form, Input, InputNumber, Row, Select } from "antd";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import ApiService from "../../../Service/ApiService";
import "./index.css";
import "../../common/forms.css";
import formatCurrency from "../../common/CurrencyFormatter";
import calculateBookingCost from "../../common/BookingCostCalculator";
import { useNavigate } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";

const CreateBooking = () => {
  const [form] = Form.useForm();
  const [hotels, setHotels] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [bookingCost, setBookingCost] = useState(0);
  const [selectedCapacity, setSelectedCapacity] = useState(0);
  const [selectedRoomCount, setSelectedRoomCount] = useState(0);
  const [availabilityStatus, setAvailabilityStatus] = useState(
    "Choose rooms and dates to verify availability.",
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const getSelectedRoomsFromForm = () => {
    const roomIds = form.getFieldValue("roomIds") || [];
    return rooms.filter((room) => roomIds.includes(room.id));
  };

  const calculateBooking = () => {
    const numOfAdults = form.getFieldValue("numOfAdults") || 0;
    const numOfChildren = form.getFieldValue("numOfChildren") || 0;
    const from = form.getFieldValue("checkInDate");
    const to = form.getFieldValue("checkOutDate");
    const selectedRooms = getSelectedRoomsFromForm();

    if (!from || !to || selectedRooms.length === 0) {
      setSelectedCapacity(selectedRooms.reduce((sum, room) => sum + (room.maxPersons || 0), 0));
      setSelectedRoomCount(selectedRooms.length);
      setAvailabilityStatus(
        selectedRooms.length > 0
          ? "Choose dates to verify availability."
          : "Choose room(s) and dates to verify availability.",
      );
      setBookingCost(0);
      return;
    }

    const totalGuests = numOfAdults + numOfChildren;
    const maxCapacity = selectedRooms.reduce(
      (sum, room) => sum + (room.maxPersons || 0),
      0,
    );
    setSelectedCapacity(maxCapacity);
    setSelectedRoomCount(selectedRooms.length);

    if (totalGuests > maxCapacity) {
      setErrorMessage(
        `Selected rooms can only accommodate ${maxCapacity} guests.`,
      );
    } else {
      setErrorMessage("");
    }

    setAvailabilityStatus(
      `Selected rooms (${selectedRooms.length}) can accommodate ${maxCapacity} guests. Availability is verified on booking submission.`,
    );

    setBookingCost(
      calculateBookingCost(
        selectedRooms,
        from.toISOString(),
        to.toISOString(),
      ),
    );
  };

  const filterRoomsByHotel = (hotelId) => {
    const selectedHotel = hotels.find((hotel) => hotel.id === hotelId);
    setSelectedHotel(selectedHotel);
  }

  useEffect(() => {
    const fetchRooms = async (hotelId) => {
      const data = await ApiService.getAllAvailableRooms(hotelId);
      if (data) {
        setRooms(data);
      }
    };
    if(selectedHotel) {
      fetchRooms(selectedHotel.id);
    }
  }, [selectedHotel]);

  useEffect(() => {
    const fetchHotels = async () => {
      const hotels = await ApiService.fetchHotels();
      if (hotels) {
        setHotels(hotels);
      }
      
    };
    fetchHotels();
  }, []);

  const submitData = async (values) => {
    setLoading(true);
    setErrorMessage("");

    const selectedRooms = rooms.filter((room) =>
      values.roomIds?.includes(room.id),
    );
    const totalGuests = (values.numOfAdults || 0) + (values.numOfChildren || 0);
    const totalCapacity = selectedRooms.reduce(
      (sum, room) => sum + (room.maxPersons || 0),
      0,
    );

    if (selectedRooms.length === 0) {
      setErrorMessage("Please select at least one room.");
      setLoading(false);
      return;
    }

    if (totalGuests > totalCapacity) {
      setErrorMessage(
        `Selected rooms can only accommodate ${totalCapacity} guests, but ${totalGuests} were entered.`,
      );
      setLoading(false);
      return;
    }

    if (!values.checkInDate || !values.checkOutDate) {
      setErrorMessage("Please select valid check-in and check-out dates.");
      setLoading(false);
      return;
    }

    const checkInDate = dayjs(values.checkInDate).format("YYYY-MM-DD");
    const checkOutDate = dayjs(values.checkOutDate).format("YYYY-MM-DD");

    const roomIsAvailable = (roomDetail) => {
      if (!roomDetail?.bookings?.length) return true;

      const requestedStart = dayjs(checkInDate);
      const requestedEnd = dayjs(checkOutDate);

      return !roomDetail.bookings.some((booking) => {
        const existingStart = dayjs(booking.checkInDate);
        const existingEnd = dayjs(booking.checkOutDate);
        return (
          requestedStart.isBefore(existingEnd) &&
          existingStart.isBefore(requestedEnd)
        );
      });
    };

    const selectedRoomDetails = await Promise.all(
      selectedRooms.map((room) => ApiService.getRoomById(room.id)),
    );

    const blockedRoom = selectedRoomDetails.find(
      (roomDetail) => !roomDetail || !roomIsAvailable(roomDetail),
    );

    if (blockedRoom) {
      setErrorMessage(
        `Room ${blockedRoom?.roomType || blockedRoom?.id} is not available for the selected dates.`,
      );
      setLoading(false);
      return;
    }

    const totalCost = calculateBookingCost(
      selectedRooms,
      values.checkInDate.toISOString(),
      values.checkOutDate.toISOString(),
    );

    const payload = {
      ...values,
      checkInDate,
      checkOutDate,
      totalNumberOfGuests: totalGuests,
      totalCost,
      totalPrice: totalCost,
    };

    const data = await ApiService.bookAdminRoom(payload);
    setLoading(false);
    if (data) navigate(-1);
  };

  return (
    <div className="create-booking-container">
      <div className="create-booking-goback">
        <ArrowLeftOutlined onClick={() => navigate(-1)} />
      </div>
      <h2>Create Booking</h2>
      <Form
        form={form}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        layout="vertical"
        className="create-booking-form"
        onFinish={(values) => submitData(values)}
      >
        <div className="create-booking-user">
          <Form.Item
            rules={[{ required: true, message: "This field is required" }]}
            name={"fullName"}
            label="Full Name"
          >
            <Input size="large" />
          </Form.Item>
          <Row className="form-row">
            <Col span={11}>
              <Form.Item
                rules={[
                  { required: true, message: "This field is required" },
                  {
                    type: "email",
                    message: "Please input a valid email address",
                  },
                ]}
                name={"email"}
                label="Email Address"
              >
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={11}>
              <Form.Item
                rules={[{ required: true, message: "This field is required" }]}
                name={"phone"}
                label="Phone Number"
              >
                <Input size="large" />
              </Form.Item>
            </Col>
          </Row>
        </div>
        <div className="create-booking-room">
          <Row className="form-row">
            <Col span={11}>
              <Form.Item
                name={"checkInDate"}
                label="Check In Date"
                rules={[
                  { required: true, message: "This field is required" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const end = getFieldValue("checkOutDate");
                      if (!value || !end || dayjs(value).isBefore(dayjs(end))) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          "Check in date must be before check out date",
                        ),
                      );
                    },
                  }),
                ]}
              >
                <DatePicker
                  size="large"
                  style={{ width: "100%" }}
                  onChange={() => calculateBooking()}
                />
              </Form.Item>
            </Col>
            <Col span={11}>
              <Form.Item
                name={"checkOutDate"}
                label="Check Out Date"
                rules={[
                  { required: true, message: "This field is required" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const start = getFieldValue("checkInDate");
                      if (
                        !value ||
                        !start ||
                        dayjs(value).isAfter(dayjs(start))
                      ) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Check out date must be after check in date"),
                      );
                    },
                  }),
                ]}
              >
                <DatePicker
                  size="large"
                  style={{ width: "100%" }}
                  onChange={() => calculateBooking()}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className="form-row">
            <Col span={11}>
              <Form.Item
                label="Select Hotel"
                name={"hotelId"}
                rules={[{ required: true, message: "This field is required" }]}
                valuePropName="value"
              >
                <Select
                  multiple={false}
                  size="large"
                  style={{ width: "100%" }}
                  placeholder="Select an item"
                  onChange={(value) => filterRoomsByHotel(value)}
                >
                  {hotels.map((hotel) => {
                    return (
                      <Select.Option key={hotel.id} value={hotel.id}>{hotel.name}</Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col span={11}>
              <Form.Item
                label="Select Room(s)"
                name={"roomIds"}
                rules={[{ required: true, message: "This field is required" }]}
                valuePropName="value"
              >
                <Select
                  mode="multiple"
                  allowClear
                  size="large"
                  style={{ width: "100%" }}
                  placeholder="Select an item"
                  onChange={() => calculateBooking()}
                >
                  {rooms.map((room) => {
                    return (
                      <Select.Option key={room.id} value={room.id}>{`${
                        room.roomType
                      } - ${formatCurrency(
                        room.roomPrice,
                        "en-US",
                        room.hotel?.currency || "USD",
                      )} per night`}</Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row className="form-row">
            <Col span={11}>
              <Form.Item
                rules={[{ required: true, message: "This field is required" }]}
                name={"numOfAdults"}
                label="Total Number of Adults"
                labelCol={{ style: { width: "100%" } }}
                wrapperCol={{ span: 24 }}
              >
                <InputNumber
                  size="large"
                  style={{ width: "100%" }}
                  onChange={() => calculateBooking()}
                />
              </Form.Item>
            </Col>
            <Col span={11}>
              <Form.Item
                rules={[{ required: true, message: "This field is required" }]}
                name={"numOfChildren"}
                label="Total Number of Children"
                labelCol={{ style: { width: "100%" } }}
                wrapperCol={{ span: 24 }}
              >
                <InputNumber
                  size="large"
                  style={{ width: "100%" }}
                  onChange={() => calculateBooking()}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>
        <div className="booking-status-panel">
          <p>
            <strong>Selected Rooms:</strong> {selectedRoomCount}
          </p>
          <p>
            <strong>Total Capacity:</strong> {selectedCapacity} guests
          </p>
          <p>
            <strong>Availability:</strong> {availabilityStatus}
          </p>
        </div>
        <div className="form-booking-details">
          {bookingCost > 0 && (
            <p>
              <span>Total Estimated Cost of booking</span>&nbsp;
              {formatCurrency(bookingCost, "en-US", "USD")}
            </p>
          )}
          {errorMessage && <p className="error-message">{errorMessage}</p>}
        </div>
        <div className="form-actions">
          <button
            className="form-actions-submit"
            type="submit"
            disabled={loading}
          >
            {loading ? "Loading..." : "Create Booking"}
          </button>
          <button type="reset" disabled={loading}>
            {loading ? "Loading..." : "Reset"}
          </button>
        </div>
      </Form>
    </div>
  );
};

export default CreateBooking;
